import { useState } from 'react';

import { verifyDeviceToken } from '../net/api';

import { setDeviceContext, setDeviceToken } from './deviceToken';

/**
 * One-time device enrollment. An admin mints a device token in web-admin's
 * Devices screen (shown once, bound to a warehouse site) and pastes it here. We
 * validate it against the API before persisting, so a bad paste never leaves
 * the device in a half-paired state. This is device+PIN auth — there is NO
 * Entra sign-in anywhere in this app.
 */
export function PairingScreen(): JSX.Element {
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const pair = (): void => {
    const trimmed = token.trim();
    if (trimmed === '') {
      setError('Paste the enrollment token from your administrator.');
      return;
    }
    setBusy(true);
    setError(null);
    void verifyDeviceToken(trimmed)
      .then((current) => {
        // Valid — persist token + site context. The token change re-renders the
        // app into the hub (useDeviceToken).
        setDeviceContext({ siteId: current.siteId, siteName: current.siteName });
        setDeviceToken(trimmed);
      })
      .catch((err: unknown) => {
        setError(
          err instanceof Error && err.message.includes('401')
            ? 'That token was rejected. Check with your administrator for a fresh one.'
            : 'Could not verify the token. Check the network connection and try again.'
        );
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div className="kiosk">
      <div className="card pairing">
        <h1>Pair this device</h1>
        <p>
          Enter the enrollment token from the WarehouseLogix admin console (Devices screen). This
          binds the scanner/tablet to one warehouse site.
        </p>
        <textarea
          value={token}
          onChange={(event) => {
            setToken(event.target.value);
          }}
          placeholder="Paste device enrollment token"
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          disabled={busy}
        />
        <button type="button" onClick={pair} disabled={busy}>
          {busy ? 'Pairing…' : 'Pair device'}
        </button>
        {error !== null && <p className="pairing__error">{error}</p>}
      </div>
    </div>
  );
}
